import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Tag,
} from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined, CrownOutlined } from '@ant-design/icons';
import { adminService } from '../../../services';
import { formatMexicoTime } from '../../../utils/timeUtils';
import type { Employee, CreateEmployeeRequest } from '../../../types';

const EmployeesPage: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  
  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchEmployees = async (page: number = currentPage, perPage: number = pageSize) => {
    try {
      setLoading(true);
      const response = await adminService.getEmployees({ 
        page, 
        per_page: perPage 
      });
      setEmployees(response.employees);
      
      // 更新分页信息
      if (response.pagination) {
        setTotal(response.pagination.total);
      }
    } catch (error) {
      console.error('获取员工列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleCreate = async (values: CreateEmployeeRequest) => {
    try {
      const response = await adminService.createEmployee(values);
      message.success(response.message);
      setModalVisible(false);
      form.resetFields();
      // 创建后刷新当前页
      fetchEmployees(currentPage, pageSize);
    } catch (error) {
      console.error('创建员工失败:', error);
    }
  };

  const handleDelete = async (employeeId: number, username: string) => {
    try {
      const response = await adminService.deleteEmployee(employeeId);
      message.success(response.message);
      // 删除后刷新当前页
      fetchEmployees(currentPage, pageSize);
    } catch (error) {
      console.error('删除员工失败:', error);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left' as const,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
      ellipsis: true,
      render: (username: string, record: Employee) => (
        <Space size="small">
          {record.is_admin ? <CrownOutlined style={{ color: '#faad14' }} /> : <UserOutlined />}
          {username}
        </Space>
      ),
    },
    {
      title: '用户类型',
      dataIndex: 'is_admin',
      key: 'is_admin',
      width: 120,
      render: (isAdmin: boolean) => (
        <Tag color={isAdmin ? 'gold' : 'blue'} icon={isAdmin ? <CrownOutlined /> : <UserOutlined />}>
          {isAdmin ? '管理员' : '员工'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => formatMexicoTime(date),
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      fixed: 'right' as const,
      render: (_: any, record: Employee) => (
        <Space>
          {!record.is_admin && (
            <Popconfirm
              title={`确定要删除用户 ${record.username} 吗？`}
              onConfirm={() => handleDelete(record.id, record.username)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          )}
          {record.is_admin && (
            <Tag color="gold" style={{ margin: 0 }}>
              系统管理员
            </Tag>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="用户管理"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            添加用户
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={employees}
          rowKey="id"
          loading={loading}
          scroll={{ x: 650 }}
          pagination={{
            current: currentPage,
            total: total,
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条用户`,
            onChange: (page, size) => {
              setCurrentPage(page);
              fetchEmployees(page, size || pageSize);
            },
            onShowSizeChange: (current, size) => {
              setCurrentPage(1);
              setPageSize(size);
              fetchEmployees(1, size);
            },
          }}
        />
      </Card>

      <Modal
        title="添加用户"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            label="用户名"
            name="username"
            rules={[{ required: true, message: '请输入用户名!' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            label="密码"
            name="password"
            rules={[
              { required: true, message: '请输入密码!' },
              { min: 6, message: '密码至少6位!' },
            ]}
          >
            <Input.Password placeholder="请输入密码" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                创建
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeesPage;
